import {
  LabraToolTipV2,
  PercentageUpDown,
  Truncate,
  VerticalDivider,
} from '@labrav/react-components'
import { useStyles } from './CustomLegends.styles'
import clsx from 'clsx'
import { isNonEmptyNumber } from '@labrav/common'
import { get } from 'lodash'

export type LegendObject = {
  id?: string
  label: string
  value?: string | JSX.Element
  legendColor: string
  legendSize: LegendSize
  icon?: JSX.Element
  isKeyValue?: boolean
  legendUpDown?: number
  legendUpDownTooltip?: string
  legendGrpahLineVisible?: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [x: string]: any
}

export const CustomLegends = ({
  legends,
  flexDirection = 'row',
  handleLegendClick,
  roundedBorder = false,
}: {
  legends: LegendObject[]
  flexDirection?: FlexDirection
  handleLegendClick?: (legend: LegendObject) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  roundedBorder?: boolean
  [x: string]: any
}) => {
  const { classes } = useStyles({ flexDirection, roundedBorder })
  return (
    <div className={classes.root} data-testid={'revenue-legend'}>
      {legends?.map((legendItem, index) => (
        <>
          <LegendsItem
            {...legendItem}
            label={legendItem?.label}
            legendSize={legendItem.legendSize}
            value={legendItem?.value}
            legendColor={legendItem?.legendColor}
            icon={legendItem?.icon}
            isKeyValue={legendItem?.isKeyValue}
            legendUpDown={legendItem?.legendUpDown}
            legendUpDownTooltip={legendItem?.legendUpDownTooltip}
            handleLegendClick={handleLegendClick}
            legendGrpahLineVisible={legendItem?.legendGrpahLineVisible}
            roundedBorder={roundedBorder}
          />
          {!handleLegendClick &&
            index !== legends?.length - 1 &&
            flexDirection === 'row' && (
              <VerticalDivider
                height={legendItem.legendSize === 'small' ? '20px' : '12px'}
              />
            )}
        </>
      ))}
    </div>
  )
}

export type LegendSize = 'small' | 'medium'
export type FlexDirection = 'row' | 'column'
type LegendsItemProps = {
  legendSize: LegendSize
  flexDirection?: FlexDirection
  label: string
  value?: string | JSX.Element
  legendColor: string
  icon?: JSX.Element
  isKeyValue?: boolean
  legendUpDown?: number
  handleLegendClick?: (legend: LegendObject) => void
  legendUpDownTooltip?: string
  roundedBorder?: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [x: string]: any
}
export const LegendsItem = (legentItemProps: LegendsItemProps) => {
  const {
    legendSize,
    label,
    value,
    legendColor,
    icon,
    isKeyValue,
    legendUpDown,
    legendUpDownTooltip,
    legendGrpahLineVisible,
    handleLegendClick,
    roundedBorder = false,
    ...props
  } = legentItemProps
  const { classes } = useStyles({
    legendSize,
    legendColor,
    isKeyValue,
    roundedBorder,
  })
  return (
    <div
      className={clsx(
        classes.legendRoot,
        handleLegendClick ? classes.legendButton : '',
        handleLegendClick && !legendGrpahLineVisible
          ? classes.lowVisibility
          : ''
      )}
      data-testid={'legend-wrapper'}
      onClick={() =>
        handleLegendClick &&
        handleLegendClick({
          legendSize,
          label,
          value,
          legendColor,
          icon,
          isKeyValue,
          legendUpDown,
          legendUpDownTooltip,
          ...props,
        })
      }
    >
      <span className={classes.leftContent}>
        <span
          data-testid={'legend-box'}
          className={roundedBorder ? classes.legendDot : classes.legendBox}
        ></span>
        {icon && icon}
        <span
          data-testid={'legend-label'}
          className={clsx(
            classes.legendLabel,
            handleLegendClick && legendGrpahLineVisible ? classes.labelText : ''
          )}
        >
          <Truncate width={'100%'} clipboardTooltip={true}>
            {label}
          </Truncate>
        </span>
      </span>
      {value && (
        <span
          data-testid={'legend-value'}
          className={clsx(
            classes.legendValue,
            isKeyValue ? classes.legendValueLight : ''
          )}
        >
          {value}
        </span>
      )}
      {isNonEmptyNumber(legendUpDown) &&
        get(props, 'rank') &&
        !!legendUpDown && (
          <LabraToolTipV2
            description={legendUpDownTooltip}
            tooltipPlacement="bottom"
          >
            <PercentageUpDown
              isPercentage={false}
              type={
                legendUpDown! > 0
                  ? 'up'
                  : legendUpDown! === 0
                    ? 'right'
                    : 'down'
              }
            />
          </LabraToolTipV2>
        )}
    </div>
  )
}
